import React from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';

export interface MarkProjectCompleteDialogProps {
  isOpen: boolean;
  projectName: string;
  isSubmitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Plain confirm/cancel dialog for marking a project complete (CDMX-16342 AC3).
 * No reason input — it only confirms intent and warns about side effects.
 */
export const MarkProjectCompleteDialog: React.FC<MarkProjectCompleteDialogProps> = ({
  isOpen,
  projectName,
  isSubmitting = false,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="md"
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <ModalOverlay />
      <ModalContent padding={'10px'} data-testid="mark-complete-dialog">
        <Stack direction={'column'}>
          <ModalHeader>Mark project complete?</ModalHeader>
          <ModalCloseButton isDisabled={isSubmitting} />
        </Stack>
        <ModalBody>
          <Text fontSize="sm" mb="3">
            You're about to mark <strong>"{projectName}"</strong> as complete.
          </Text>
          <Text fontSize="sm" color="gray.600">
            This closes any remaining open work items and stops new assignments. This
            can't be undone.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr="3"
            onClick={onCancel}
            isDisabled={isSubmitting}
            data-testid="mark-complete-cancel"
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={onConfirm}
            isLoading={isSubmitting}
            data-testid="mark-complete-confirm"
          >
            Mark Complete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

MarkProjectCompleteDialog.displayName = 'MarkProjectCompleteDialog';
export default MarkProjectCompleteDialog;
